import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { ManualExpenseEndPointLogic } from '../logic/Finance/ProviderExpense/ManualExpenseEndpointLogic';
import { selectUserToken, setDisplayErrorSnackBar } from '../features/userSlice';

export const useManualExpenseFileUpload = (shared_uid: any, page: string) => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as string;
  const [isDragging, setIsDragging] = useState(false);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any>([]); // store files which are
  const [files, setFiles] = useState<any>([]); // store files which are we selected brower or drag and drop (when edit a task)
  const [selectedFiles, setSelectedFiles] = useState<any>([]);

  const processFiles = (newFiles: any) => {
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    newFiles.forEach((file: any) => {
      const fileSizeMB = file.size / (1024 * 1024);
      // Check file size limit
      if (fileSizeMB > 5) {
        //setUploadError('File size exceeds the 5MB limit.');
        dispatch(setDisplayErrorSnackBar({ message: `File size exceeds the 5MB limit.`, type: "error" }));
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        dispatch(setDisplayErrorSnackBar({ message: `Invalid file type: ${file.name}. Only PDF, JPG, and PNG allowed.`, type: "error" }));
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(",")[1]; // Extract base64 part
        const newFile = {
          file: file.name,
          size: file.size,
          base64: base64String || "", // Base64 encoded string
          progress: 100, // Simulated progress
          status: "Uploaded", // Add status here
        };
        if (page === "create_page")
          setUploadedFiles((prevFiles: any) => [...prevFiles, newFile]);
        if (page === "edit_page")
          setSelectedFiles((prevFiles: any) => [...prevFiles, newFile]);
      };
    });
  };

  const handleFileUpload = (event: any) => {
    const selectedFiles = Array.from(event.target.files);
    processFiles(selectedFiles);
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prevFiles: File[]) =>
      prevFiles.filter((_, i) => i !== index)
    );
  };

  const handleFileDrop = (event: any) => {
    event.preventDefault();
    setIsDragging(false);
    const newFiles = Array.from(event.dataTransfer.files);
    processFiles(newFiles);
  };

  const fetchFilesManualExpense = async(shared_uid:any) => {
    setFileUploadLoading(true);
    try {
      await ManualExpenseEndPointLogic.getFiles(oauthTokenHeader, shared_uid, setFiles);
    } catch (error) {
      dispatch({ type: "error", payload: { message: error } });
    } finally {
      setFileUploadLoading(false);
    }
  };

  const uploadFilesManualExpense = async (shared_uid:any) => {
    setFileUploadLoading(true);
    try {
      let choosedFiles = page === "edit_page" ? selectedFiles : uploadedFiles;
      const uploadPromises = choosedFiles.map((file: any) => {
        const fileUploadBody = {
          object_type: "manual_adjustment_expenses",
          file_name: file.file,
          file: file.base64,
        }
        return ManualExpenseEndPointLogic.fileUploadME(oauthTokenHeader, fileUploadBody, shared_uid, dispatch);
      });
      await Promise.all(uploadPromises);
      if (page === "create_page") setUploadedFiles([]);
      if (page === "edit_page") {
        setSelectedFiles([]);
        fetchFilesManualExpense(shared_uid); // Refresh files after upload
      }
    } catch (error:any) {
      dispatch({ type: "error", payload: { message: error } });
    } finally {
      setFileUploadLoading(false);
    }
  };

  useEffect(() => {
    if (selectedFiles.length === 0) return;
    uploadFilesManualExpense(shared_uid);
    // eslint-disable-next-line
  }, [selectedFiles]);

  return {
    uploadedFiles,
    handleFileUpload,
    setUploadedFiles,
    handleRemoveFile,
    handleFileDrop,
    isDragging,
    setIsDragging,
    fileUploadLoading,
    setFileUploadLoading,
    fetchFilesManualExpense,
    files,
    setFiles,
    uploadFilesManualExpense,
  };
}
